<template>
  <div class="circle-progress-container" :style="{ width: size + 'px', height: size + 'px' }">
    <div ref="chartContainer" class="chart-container"></div>

    <div class="content">
      <div class="label">{{ label }}</div>
      <div class="value" :class="[valueColorClass, valueFontClass]">
        <slot>
          <span>{{ Math.round(currentValue) }}</span>
          <span v-if="unit" class="unit">{{ unit }}</span>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CircleProgress",
  props: {
    // 圆环大小
    size: {
      type: Number,
      default: 260
    },
    // 进度值(0-100)
    value: {
      type: [Number, String],
      required: true
    },
    // 单位（如：%）
    unit: {
      type: String,
      default: ""
    },
    // 顶部标签
    label: {
      type: String,
      default: ""
    },
    // 是否开启动画
    animated: {
      type: Boolean,
      default: true
    },
    // 动画持续时间（毫秒）
    animationDuration: {
      type: Number,
      default: 1000
    },
    // 动画缓动效果
    animationEasing: {
      type: String,
      default: 'cubicOut'
    },
    // 圆点颜色 (保留原属性，虽然在新逻辑中可能不直接使用)
    dotColor: {
      type: String,
      default: '#FFFFFF'
    },
    // 是否启用脉冲动画效果 (保留原属性，虽然在新逻辑中可能不直接使用)
    enablePulse: {
      type: Boolean,
      default: false
    },
    // 脉冲动画周期 (毫秒) (保留原属性，虽然在新逻辑中可能不直接使用)
    pulseDuration: {
      type: Number,
      default: 1500
    },
    // --- 新增属性 ---
    // 指示器颜色
    indicatorColor: {
      type: String,
      default: 'red' // 默认为红色
    },
    // 指示器大小（占圆环的百分比，例如 2 表示 2%）
    indicatorSize: {
      type: Number,
      default: 2 // 默认为圆环的 2%
    }
  },

  data() {
    return {
      // 图表实例
      chart: null,
      // 当前显示值
      currentValue: 0
    };
  },

  mounted() {
    this.$nextTick(() => {
      // 初始化图表
      this.initChart();
    });
  },

  beforeDestroy() {
    // 销毁图表实例
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },

  computed: {
    // 根据标签返回不同的颜色类名
    valueColorClass() {
      if (this.label === '超越全国用户') {
        return 'exceed-users';
      } else if (this.label === '身体状况') {
        return 'exceed-users';
      }
      return '';
    },

    // 根据标签获取不同的渐变色设置
    gradientColors() {
      if (this.label === '超越全国用户') {
        // 蓝色渐变
        return [
          { offset: 0, color: '#2873FE' },
          { offset: 0.5, color: '#3896FE' },
          { offset: 1, color: '#49BEFE' }
        ];
      } else if (this.label === '身体状况') {
        return [
          { offset: 0, color: '#2873FE' },
          { offset: 0.5, color: '#3896FE' },
          { offset: 1, color: '#49BEFE' }
        ];
      }
      // 默认渐变
      return [
        { offset: 0, color: '#f0f791' },
        { offset: 0.3, color: '#eef591' },
        { offset: 1, color: '#c8a2c8' }
      ];
    },
    // 计算属性，判断value是否为数字，返回对应字体类名
    valueFontClass() {
      // 尝试将值转换为数字
      const numValue = Number(this.value);
      // 检查转换结果是否是有效数字 (不是 NaN)
      // 并且原始值不是空字符串 (因为 Number('') === 0)
      // 同时也处理字符串形式的数字
      return (!isNaN(numValue) && this.value !== '') || (typeof this.value === 'string' && !isNaN(parseFloat(this.value)) && this.value.trim() !== '')
             ? 'is-numeric'
             : '';
    }
  },

  methods: {
    // 初始化图表
    initChart() {
      const container = this.$refs.chartContainer;
      if (!container) return;

      // --- 确保 ECharts 已加载 ---
      // 如果你没有全局注册 $echarts, 你需要在这里引入
      // import * as echarts from 'echarts/core';
      // import { PieChart } from 'echarts/charts';
      // import { CanvasRenderer } from 'echarts/renderers';
      // echarts.use([PieChart, CanvasRenderer]);
      // this.chart = echarts.init(container);
      // --- 或者如果你是全局注册的 ---
      if (!this.$echarts) {
         console.error("ECharts instance not found. Make sure it's globally registered or imported locally.");
         return;
      }
      this.chart = this.$echarts.init(container);
      // --------------------------

      // 首次更新图表
      this.updateChart();
    },

    // 获取图表配置
    getChartOption() {
      // 解析进度值
      const progressValue = parseFloat(this.value);
      // 如果传入的是非数字字符串，则显示0，但保留原始字符串用于字体判断
      const displayValue = isNaN(progressValue) ? 0 : progressValue;


      // 标准化进度值到0-100范围
      const normalizedValue = Math.max(0, Math.min(100, displayValue));
      // 更新内部显示值，如果是NaN则显示0
      this.currentValue = isNaN(progressValue) ? 0 : normalizedValue;


      // 指示器大小，确保不小于0
      const indicatorSegmentSize = Math.max(0, this.indicatorSize);

      // 修改：调整进度条的长度，使其比实际进度短2%
      // 如果进度值小于指示器大小，则进度条不显示
      const adjustedProgressValue = normalizedValue > indicatorSegmentSize ?
                                   normalizedValue - indicatorSegmentSize : 0;

      // 修改：指示器位置计算
      // 如果进度为0，指示器也在0位置
      // 否则指示器位于调整后的进度条末端
      const indicatorPosition = normalizedValue > 0 ? adjustedProgressValue : 0;

      return {
        animation: this.animated,
        animationDuration: this.animationDuration,
        animationEasing: this.animationEasing,
        series: [
          // 1. 背景圆环 (最底层)
          {
            type: 'pie',
            radius: ['80%', '92%'], // 内外半径
            center: ['50%', '50%'], // 圆心位置
            startAngle: 270,        // 起始角度 (12点钟方向)
            clockwise: true,        // 顺时针
            avoidLabelOverlap: false,
            hoverAnimation: false,
            silent: true,           // 不响应鼠标事件
            labelLine: { show: false },
            label: { show: false },
            data: [
              {
                value: 100,
                name: 'background',
                itemStyle: {
                  color: '#8292B8', // 更柔和的背景色，可调整
                  borderWidth: 0
                }
              }
            ]
          },
          // 2. 进度圆环 (中间层) - 调整为比实际进度短的部分
          {
            type: 'pie',
            radius: ['80%', '92%'],
            center: ['50%', '50%'],
            startAngle: 270,
            clockwise: true,
            avoidLabelOverlap: false,
            hoverAnimation: false,
            silent: true,
            labelLine: { show: false },
            label: { show: false },
            data: [
              {
                value: adjustedProgressValue, // 修改：使用调整后的进度值
                name: 'progress',
                itemStyle: {
                  color: { // 应用渐变色
                    type: 'linear',
                    x: 0, y: 0, x2: 0, y2: 1,
                    colorStops: this.gradientColors,
                    borderRadius: 10
                  },
                  borderRadius: 10
                }
              },
              {
                value: 100 - adjustedProgressValue, // 修改：调整剩余部分
                name: 'empty',
                itemStyle: {
                  color: 'transparent', // 完全透明
                  borderWidth: 0
                }
              }
            ]
          },
          // 3. 指示器圆环 (最顶层)
          {
            type: 'pie',
            radius: ['80%', '92%'], // 必须与进度条半径一致
            center: ['50%', '50%'],
            startAngle: 270,
            clockwise: true,
            avoidLabelOverlap: false,
            hoverAnimation: false,
            silent: true,
            labelLine: { show: false },
            label: { show: false },
            // 设置更高的z值确保指示器在最上层
            z: 3,
            data: [
              {
                // 指示器之前的透明部分
                value: indicatorPosition-2,
                name: 'transparentBefore',
                itemStyle: { color: 'transparent' }
              },
              {
                // 指示器部分
                value: normalizedValue > 0 ? indicatorSegmentSize : 0, // 只在有进度时显示指示器
                name: 'indicator',
                itemStyle: {
                   // 使用渐变色的第一个颜色而不是最后一个颜色
                   // 因为指示器位于进度条末端，应该使用与其相符的颜色
                   color: this.gradientColors[0].color,
                   borderColor: '#FFFFFF',
                   borderWidth: 2,
                   borderRadius: indicatorSegmentSize > 0 ? 10 : 0
                }
              },
              {
                 // 指示器之后的透明部分
                value: 100 - indicatorPosition - (normalizedValue > 0 ? indicatorSegmentSize : 0),
                name: 'transparentAfter',
                itemStyle: { color: 'transparent' }
              }
            ]
          }
        ]
      };
    },

    // 更新图表
    updateChart() {
      if (!this.chart) return;

      const option = this.getChartOption();
      // 使用 notMerge: true 确保旧配置被完全替换，防止系列叠加出问题
      // 使用 replaceMerge: ['series'] 也可以，只替换series
      this.chart.setOption(option, true);
    }
  },

  watch: {
    // 监听所有可能影响图表渲染的 props
    value: 'updateChart',
    size: {
      handler() {
        this.$nextTick(() => {
          if (this.chart) {
            this.chart.resize(); // 容器大小变化时调整图表大小
             this.updateChart(); // 重新获取配置可能更安全，以防半径等受size影响
          }
        });
      }
    },
    label: 'updateChart', // 标签变化影响渐变色
    indicatorColor: 'updateChart', // 指示器颜色变化
    indicatorSize: 'updateChart' // 指示器大小变化
  }
};
</script>

<style lang="scss" scoped>
.circle-progress-container {
  position: relative;
  pointer-events: none;
  margin: 0 auto; /* 保持居中 */

  .chart-container {
    width: 100%;
    height: 100%;
  }

  .content {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center; /* 确保文字居中 */
    pointer-events: none; /* 允许点击穿透图表 */

    .label {
      font-size: 22px; /* 根据需要调整 */
      color: #333333;
      margin-bottom: 5px; /* 调整标签和数值间距 */
      line-height: 1.2; /* 调整行高 */
    }

    .value {
      font-size: 65px; /* 根据需要调整 */
      line-height: 1; /* 紧凑行高 */
      font-weight: 500;

      &.is-numeric {
        font-size: 65px;
        font-family: AccidentalPresidency; /* 仅数字时应用特殊字体 */
      }

      .unit {
        font-size: 65px; /* 单位字体大小 */
        margin-left: 2px; /* 微调单位和数值间距 */
      }

      /* 不同标签对应的文字颜色 */
      &.exceed-users {
        color: #2976FE;
      }

      &.body-status {
        color: #2976FE;
      }
    }
  }
}
</style>
